import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { getInitials } from '@/utils/strings';
import { getCurrentUserProfile } from '@/api/client';
import { usePlatform } from '@/hooks/usePlatform';

export type UserRole = 'Employee' | 'AdminOU' | 'AdminGlobal';

const VALID_ROLES: UserRole[] = ['Employee', 'AdminOU', 'AdminGlobal'];

export interface CurrentUser {
  id: string;
  displayName: string;
  email: string;
  tenantId: string;
  roles: UserRole[];
  initials: string;
  primaryRole: UserRole;
  preferredLanguage?: string;
  givenName?: string;
}

/**
 * Usuario actual. La IDENTIDAD viene de MSAL (Azure AD); los ROLES y el preferredLanguage
 * vienen de la BD de la app (UserAdminService → ResolveByEmail), NO del token. Azure AD solo
 * autentica; los roles y el idioma se gestionan en la aplicación. Fail-soft: si no se resuelve
 * el perfil → roles Employee, preferredLanguage undefined.
 */
export function useCurrentUser(): CurrentUser | null {
  const { accounts } = useMsal();
  const { teamsContext } = usePlatform();
  const account = accounts[0] ?? null;
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [preferredLanguage, setPreferredLanguage] = useState<string | undefined>(undefined);

  // Identidad: dentro de Teams viene del contexto de Teams (SSO); en navegador, de MSAL.
  // Se usan primitivos para mantener estables las dependencias de los hooks.
  const id = teamsContext?.userId || account?.localAccountId || '';
  const email = teamsContext?.userPrincipalName || account?.username || '';
  // El AD trae el displayName como "Apellidos Nombre". Para mostrar "Nombre Apellidos" SIN
  // modificar el AD, componemos desde los claims separados given_name (nombre) + family_name
  // (apellidos) cuando ambos vienen en el token. Si no, fallback al displayName de siempre.
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const givenName = typeof claims.given_name === 'string' ? claims.given_name : '';
  const familyName = typeof claims.family_name === 'string' ? claims.family_name : '';
  const composedName = givenName && familyName ? `${givenName} ${familyName}` : '';
  const name = composedName || teamsContext?.displayName || account?.name || email || 'Usuario';
  const tenantId = teamsContext?.tenantId || account?.tenantId || '';
  const hasIdentity = Boolean(id || email);

  useEffect(() => {
    let cancelled = false;
    if (!email) {
      setRoles([]);
      setPreferredLanguage(undefined);
      return;
    }
    getCurrentUserProfile(email)
      .then((profile) => {
        if (cancelled) return;
        const valid = profile.roles.filter((r): r is UserRole => VALID_ROLES.includes(r as UserRole));
        setRoles(valid.length > 0 ? valid : ['Employee']);
        setPreferredLanguage(profile.preferredLanguage);
      })
      .catch((error) => {
        if (import.meta.env.DEV) console.error('useCurrentUser: failed to load profile, defaulting to Employee', error);
        if (!cancelled) {
          setRoles(['Employee']);
          setPreferredLanguage(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  return useMemo(() => {
    if (!hasIdentity) return null;
    const effectiveRoles: UserRole[] = roles.length > 0 ? roles : ['Employee'];
    return {
      id,
      displayName: name,
      email,
      tenantId,
      roles: effectiveRoles,
      initials: getInitials(name),
      primaryRole: effectiveRoles.includes('AdminGlobal')
        ? 'AdminGlobal'
        : effectiveRoles.includes('AdminOU')
          ? 'AdminOU'
          : 'Employee',
      preferredLanguage,
      givenName
    };
  }, [hasIdentity, id, name, email, tenantId, roles, preferredLanguage, givenName]);
}
